import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Camera, Check, ImageOff, Loader2, Trash2, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/Button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/Dialog';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/Select';
import { Textarea } from '../ui/Textarea';
import { useStudents } from '../../contexts/StudentsContext';
import { GENDER_OPTIONS, type Gender, type StudentFormValues, emptyStudentForm } from '../../types/student';
import { describeFirebaseError } from '../../utils/firebase';
const MAX_PHOTO_BYTES = 5 * 1024 * 1024;
const UNSPECIFIED = 'unspecified';
export function StudentFormModal() {
  const {
    formOpen,
    editing,
    closeForm,
    createStudent,
    updateStudent
  } = useStudents();
  const [values, setValues] = useState<StudentFormValues>(emptyStudentForm);
  const [photo, setPhoto] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [removePhoto, setRemovePhoto] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (!formOpen) return;
    setPhoto(null);
    setRemovePhoto(false);
    setPreviewUrl('');
    if (editing) {
      setValues({
        fullNames: editing.fullNames,
        gender: editing.gender,
        className: editing.className,
        fatherName: editing.fatherName,
        fatherPhone: editing.fatherPhone,
        motherName: editing.motherName,
        motherPhone: editing.motherPhone,
        note: editing.note
      });
    } else {
      setValues(emptyStudentForm);
    }
  }, [formOpen, editing]);
  useEffect(() => {
    if (!photo) {
      setPreviewUrl('');
      return;
    }
    const url = URL.createObjectURL(photo);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [photo]);
  const shownPhoto = previewUrl || (!removePhoto ? editing?.photoURL ?? '' : '');
  const isEmptyRecord = useMemo(() => Object.values(values).every(value => !String(value).trim()) && !photo, [values, photo]);
  const update = (key: keyof StudentFormValues, value: string) => setValues(current => ({
    ...current,
    [key]: value
  }));
  const handlePickPhoto = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file.');
      return;
    }
    if (file.size > MAX_PHOTO_BYTES) {
      toast.error('That photo is larger than 5 MB.', {
        description: 'Pick a smaller image and try again.'
      });
      return;
    }
    setPhoto(file);
    setRemovePhoto(false);
  };
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isEmptyRecord && !editing) {
      toast.warning('Add at least one detail before saving.');
      return;
    }
    setSaving(true);
    try {
      const label = values.fullNames.trim() || 'Untitled record';
      const result = editing ? await updateStudent(editing, values, photo, removePhoto) : await createStudent(values, photo);
      if (result === 'pending') {
        toast.warning('Saved on this device', {
          description: `${label} is queued and will sync to Firestore as soon as the connection returns.`
        });
      } else {
        toast.success(editing ? 'Student record updated' : 'Student saved to Firestore', {
          description: label
        });
      }
      closeForm();
    } catch (error) {
      toast.error('Could not save the record', {
        description: describeFirebaseError(error)
      });
    } finally {
      setSaving(false);
    }
  };
  return <Dialog open={formOpen} onOpenChange={open => open ? undefined : closeForm()} data-id="element-270" data-mp="6jtrbizql0vm">
      <DialogContent className="glass-strong max-h-[92vh] max-w-2xl overflow-y-auto scrollbar-slim rounded-3xl" data-id="element-271" data-mp="3mai3qe1k7lmk">
        <DialogHeader data-id="element-272" data-mp="3r9us5382x0r9">
          <DialogTitle className="flex items-center gap-2 text-lg" data-id="element-273" data-mp="3ra4hqpp7p5wf">
            <span className="grid h-9 w-9 place-items-center rounded-xl brand-gradient text-white" data-id="element-274" data-mp="3da3h9bcv00hh">
              <UserPlus className="h-4 w-4" data-id="element-275" data-mp="3i84xzpmtbnps" />
            </span>
            {editing ? 'Edit student record' : 'Add a new student'}
          </DialogTitle>
          <DialogDescription data-id="element-276" data-mp="9tmum17tgwci">
            Every field is optional — save what you have now and complete the rest later.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 py-2" data-id="element-277" data-mp="28qth8u1adk">
          <div className="flex flex-col gap-4 rounded-2xl border border-[var(--glass-hairline)] bg-white/50 p-4 sm:flex-row sm:items-center dark:bg-white/5" data-id="element-278" data-mp="51lda0vskffm">
            <div className="relative mx-auto h-24 w-24 shrink-0 overflow-hidden rounded-2xl border border-[var(--glass-border)] bg-muted sm:mx-0" data-id="element-279" data-mp="spmh4wi0z7op">
              {shownPhoto ? <img src={shownPhoto} alt="Student photo preview" className="h-full w-full object-cover" data-id="element-280" data-mp="16vofbqqt5va3" /> : <span className="grid h-full w-full place-items-center text-muted-foreground" data-id="element-281" data-mp="12ttgcswcntzh">
                  <ImageOff className="h-6 w-6" data-id="element-282" data-mp="1gp576p7dx0b4" />
                </span>}
            </div>
            <div className="min-w-0 flex-1 text-center sm:text-left" data-id="element-283" data-mp="1bqahr4ewwdi1">
              <p className="text-sm font-semibold text-foreground" data-id="element-284" data-mp="nyvl9s9tgqep">Student photo</p>
              <p className="mt-0.5 text-xs text-muted-foreground" data-id="element-285" data-mp="jxtij1goxw7z">
                Uploaded to Firebase Storage under <span className="font-mono" data-id="element-286" data-mp="xxchp4rixx85">student_photos/</span>. JPG or PNG, max
                5&nbsp;MB.
              </p>
              <div className="mt-3 flex flex-wrap justify-center gap-2 sm:justify-start" data-id="element-287" data-mp="szjcady1p78x">
                <Button type="button" variant="outline" className="h-9 rounded-xl" onClick={() => fileInputRef.current?.click()} data-id="element-288" data-mp="299vf6fs8lscp">
                  <Camera className="mr-1.5 h-4 w-4" data-id="element-289" data-mp="2cfnordzmo2tl" />
                  {shownPhoto ? 'Replace photo' : 'Upload photo'}
                </Button>
                {shownPhoto && <Button type="button" variant="ghost" className="h-9 rounded-xl text-destructive" onClick={() => {
                setPhoto(null);
                setRemovePhoto(true);
              }} data-id="element-290" data-mp="2lesm4gzgdq46">
                    <Trash2 className="mr-1.5 h-4 w-4" data-id="element-291" data-mp="1xm2hkstvq93x" />
                    Remove
                  </Button>}
              </div>
              <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handlePickPhoto} aria-label="Upload student photo" data-id="element-292" data-mp="27f9qgnntw78r" />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2" data-id="element-293" data-mp="17dd27wqx5pr2">
            <div className="space-y-2 sm:col-span-2" data-id="element-294" data-mp="17c1yty1dyk0h">
              <Label htmlFor="fullNames" data-id="element-295" data-mp="12gmo3eyod48a">Full names</Label>
              <Input id="fullNames" value={values.fullNames} onChange={event => update('fullNames', event.target.value)} placeholder="e.g. Aline Uwase" className="h-10 rounded-xl" autoComplete="off" data-id="element-296" data-mp="xfylrx4zx0yr" />
            </div>

            <div className="space-y-2" data-id="element-297" data-mp="jnshb8b7gif5">
              <Label htmlFor="className" data-id="element-298" data-mp="jmqvc8snibin">Class</Label>
              <Input id="className" value={values.className} onChange={event => update('className', event.target.value)} placeholder="e.g. Primary 4B" className="h-10 rounded-xl" autoComplete="off" data-id="element-299" data-mp="eqsct1u07nq5" />
            </div>

            <div className="space-y-2" data-id="element-300" data-mp="qradhy3c88k">
              <Label htmlFor="gender" data-id="element-301" data-mp="1omx7tig5ld64">Gender</Label>
              <Select value={values.gender || UNSPECIFIED} onValueChange={next => update('gender', (next === UNSPECIFIED ? '' : next) as Gender)} data-id="element-302" data-mp="1on71bg0eq2gk">
                <SelectTrigger id="gender" className="h-10 w-full rounded-xl" data-id="element-303" data-mp="1eny5ehlzedrk">
                  <SelectValue placeholder="Select gender" data-id="element-304" data-mp="1jmjb2wk05pne" />
                </SelectTrigger>
                <SelectContent data-id="element-305" data-mp="15v4pqj2mhcd6">
                  <SelectItem value={UNSPECIFIED} data-id="element-306" data-mp="15uuzqvhkv57h">Not specified</SelectItem>
                  {GENDER_OPTIONS.map(gender => <SelectItem key={gender} value={gender} data-id="element-307" data-mp="vy02sh0ydcvb">
                      {gender}
                    </SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2" data-id="element-308" data-mp="mws3pwqm4806">
              <Label htmlFor="fatherName" data-id="element-309" data-mp="mxtpow962ewo">Father&apos;s name</Label>
              <Input id="fatherName" value={values.fatherName} onChange={event => update('fatherName', event.target.value)} placeholder="Father's full name" className="h-10 rounded-xl" autoComplete="off" data-id="element-310" data-mp="qz57iflstvub" />
            </div>

            <div className="space-y-2" data-id="element-311" data-mp="hckssfzhmzjy">
              <Label htmlFor="fatherPhone" data-id="element-312" data-mp="ccgb4mmufys5">Father&apos;s telephone</Label>
              <Input id="fatherPhone" type="tel" value={values.fatherPhone} onChange={event => update('fatherPhone', event.target.value)} placeholder="+250 7xx xxx xxx" className="h-10 rounded-xl" autoComplete="off" data-id="element-313" data-mp="ceay06hoe0yv" />
            </div>

            <div className="space-y-2" data-id="element-314" data-mp="w1gyw7hzi2hx">
              <Label htmlFor="motherName" data-id="element-315" data-mp="w2jpeebea2xb">Mother&apos;s name</Label>
              <Input id="motherName" value={values.motherName} onChange={event => update('motherName', event.target.value)} placeholder="Mother's full name" className="h-10 rounded-xl" autoComplete="off" data-id="element-316" data-mp="1a0gll9lxs7bg" />
            </div>

            <div className="space-y-2" data-id="element-317" data-mp="vvooht5jn6o3">
              <Label htmlFor="motherPhone" data-id="element-318" data-mp="vwqdl1je9tot">Mother&apos;s telephone</Label>
              <Input id="motherPhone" type="tel" value={values.motherPhone} onChange={event => update('motherPhone', event.target.value)} placeholder="+250 7xx xxx xxx" className="h-10 rounded-xl" autoComplete="off" data-id="element-319" data-mp="qwu8eyyynw7o" />
            </div>

            <div className="space-y-2 sm:col-span-2" data-id="element-320" data-mp="1eo9ff51on1p9">
              <Label htmlFor="note" data-id="element-321" data-mp="19nv7ro3zttrr">Small note</Label>
              <Textarea id="note" value={values.note} onChange={event => update('note', event.target.value)} placeholder="Allergies, transport arrangement, anything worth remembering…" rows={3} className="rounded-xl" data-id="element-322" data-mp="19pfyf2n6sskc" />
            </div>
          </div>

          <div className="flex flex-col-reverse gap-2 border-t border-[var(--glass-hairline)] pt-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-323" data-mp="3p98iyll3e712">
            <p className="text-xs text-muted-foreground" data-id="element-324" data-mp="2sw2lmwg8vsx">Partial records are saved cleanly — nothing is required.</p>
            <div className="flex flex-col-reverse gap-2 sm:flex-row" data-id="element-325" data-mp="6uzdoaf7hydo">
              <Button type="button" variant="ghost" className="h-10 rounded-xl" onClick={closeForm} disabled={saving} data-id="element-326" data-mp="6wu2hztlman1">
                Cancel
              </Button>
              <Button type="submit" disabled={saving} className="h-10 rounded-xl brand-gradient px-6 text-white shadow-lg shadow-indigo-500/25 hover:opacity-95" data-id="element-327" data-mp="gt6pyfyjvs9c">
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" data-id="element-328" data-mp="2nj3hz8qulhzc" /> : <Check className="mr-2 h-4 w-4" data-id="element-329" data-mp="2njwg5lbqqz5v" />}
                {saving ? 'Saving…' : editing ? 'Save changes' : 'Submit record'}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>;
}